import { createContext, useContext, useEffect, useState, useMemo, ReactNode } from "react";

/**
 * In-app accessibility override for motion.
 *
 * Tri-state preference:
 *   - "system": follow `prefers-reduced-motion` from the OS (default)
 *   - "reduced": force reduced motion regardless of OS setting
 *   - "full":   force full motion regardless of OS setting
 *
 * The resolved boolean (`reduced`) is mirrored to the document element via:
 *   - `data-motion-pref` attribute (the user choice; "system" | "reduced" | "full")
 *   - `data-motion-reduced` attribute ("true" | "false" — the resolved value)
 *   - `motion-reduce` class on <html> when reduced (CSS uses this to flatten
 *     all non-framer animations regardless of OS state)
 *
 * `src/lib/motion.ts#prefersReducedMotion` reads `data-motion-reduced` so that
 * `splitChars` and `letterRise` (evaluated at render time) honour the override.
 * `<MotionConfig reducedMotion>` in App.tsx reacts to the same source via the
 * `useMotionPreference` hook (`"always" | "never" | "user"`).
 */

export type MotionPreference = "system" | "reduced" | "full";

interface MotionPreferenceContextType {
  preference: MotionPreference;
  reduced: boolean;
  setPreference: (p: MotionPreference) => void;
  /** Maps to framer-motion's MotionConfig `reducedMotion` prop. */
  motionConfigValue: "always" | "never" | "user";
}

const STORAGE_KEY = "motion-preference";

function readStoredPreference(): MotionPreference {
  if (typeof window === "undefined") return "system";
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "reduced" || v === "full" ? v : "system";
}

function osPrefersReduced(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function resolve(pref: MotionPreference): boolean {
  if (pref === "reduced") return true;
  if (pref === "full") return false;
  return osPrefersReduced();
}

function applyToDocument(pref: MotionPreference, reduced: boolean) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.motionPref = pref;
  root.dataset.motionReduced = reduced ? "true" : "false";
  root.classList.toggle("motion-reduce", reduced);
}

// Apply the persisted preference synchronously on module load, before React
// paints, so the first frame already honours the override (no flash of motion).
if (typeof document !== "undefined") {
  const initial = readStoredPreference();
  applyToDocument(initial, resolve(initial));
}

const MotionPreferenceContext = createContext<MotionPreferenceContextType>({
  preference: "system",
  reduced: false,
  setPreference: () => {},
  motionConfigValue: "user",
});

export const useMotionPreference = () => useContext(MotionPreferenceContext);

export const MotionPreferenceProvider = ({ children }: { children: ReactNode }) => {
  const [preference, setPreferenceState] = useState<MotionPreference>(() => readStoredPreference());
  const [reduced, setReduced] = useState<boolean>(() => resolve(readStoredPreference()));

  const setPreference = (p: MotionPreference) => {
    setPreferenceState(p);
    try {
      window.localStorage.setItem(STORAGE_KEY, p);
    } catch {
      /* storage may be unavailable in some browsing modes */
    }
  };

  // Re-resolve and sync to <html> whenever preference changes.
  useEffect(() => {
    const r = resolve(preference);
    setReduced(r);
    applyToDocument(preference, r);
  }, [preference]);

  // Listen to OS changes only when following system.
  useEffect(() => {
    if (preference !== "system") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => {
      const r = mq.matches;
      setReduced(r);
      applyToDocument("system", r);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [preference]);

  const value = useMemo<MotionPreferenceContextType>(
    () => ({
      preference,
      reduced,
      setPreference,
      motionConfigValue: preference === "reduced" ? "always" : preference === "full" ? "never" : "user",
    }),
    [preference, reduced],
  );

  return <MotionPreferenceContext.Provider value={value}>{children}</MotionPreferenceContext.Provider>;
};
